/**
 * Tenant-Admin → Bulk Imports.
 *
 * Five tabs (Owners, Shops, Categories, Products, Customers) each with:
 *  - Download template (CSV with sample row)
 *  - Drag-drop or pick-file uploader
 *  - Result summary (created / updated / errors) + per-row report
 *
 * S3 base URL is set at the top of the page so all rows uploaded after that
 * resolve their ``*_s3_key`` columns into public image URLs automatically.
 */
import { useEffect, useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TABS = [
    { key: "owners", label: "Shop Owners", help: "Onboard owners and link them to their shops by name." },
    { key: "shops", label: "Shops", help: "Bulk-add restaurants with logos, addresses, commission, and vertical." },
    { key: "categories", label: "Categories", help: "Menu/product categories per shop (single-level; use parent_name for subcategories)." },
    { key: "products", label: "Products", help: "Menu items with prices, images, and stock. Categories are matched by name." },
    { key: "customers", label: "Customers", help: "Customers with default address, wallet balance, and coin balance." },
];

export default function AdminImports() {
    const [active, setActive] = useState("owners");
    const [s3, setS3] = useState("");
    const [savingS3, setSavingS3] = useState(false);

    useEffect(() => {
        api.get("/admin/imports/s3-config")
            .then(({ data }) => setS3(data?.s3_base_url || ""))
            .catch(() => null);
    }, []);

    const saveS3 = async () => {
        setSavingS3(true);
        try {
            await api.patch("/admin/imports/s3-config", { s3_base_url: s3 });
            toast.success("S3 base URL saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSavingS3(false); }
    };

    return (
        <div data-testid="admin-imports-page">
            <h1 className="text-2xl font-bold">Bulk Imports</h1>
            <p className="text-sm text-muted-foreground mt-1">Upload CSV files to seed shop owners, shops, categories, products, and customers in one shot.</p>

            <section className="mt-4 bg-card border border-border rounded-2xl p-4">
                <h3 className="font-bold text-sm">AWS S3 base URL (for image keys)</h3>
                <p className="text-xs text-muted-foreground mb-2">e.g. <code className="bg-muted px-1 py-0.5 rounded">https://your-bucket.s3.ap-south-1.amazonaws.com</code>. Image keys in every CSV are auto-prefixed with this URL.</p>
                <div className="flex gap-2">
                    <input
                        data-testid="s3-base-input"
                        value={s3}
                        onChange={(e) => setS3(e.target.value)}
                        placeholder="https://bucket.s3.amazonaws.com"
                        className="flex-1 bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                    />
                    <button
                        data-testid="save-s3-base"
                        onClick={saveS3}
                        disabled={savingS3}
                        className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
                    >{savingS3 && <Loader2 size={14} className="animate-spin" />} Save</button>
                </div>
            </section>

            <div className="mt-5 flex gap-1.5 overflow-x-auto no-scrollbar">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`imports-tab-${t.key}`}
                        onClick={() => setActive(t.key)}
                        className={`px-3 py-2 rounded-xl text-sm font-bold whitespace-nowrap ${active === t.key ? "bg-foreground text-background" : "bg-card border border-border"}`}
                    >{t.label}</button>
                ))}
            </div>

            <ImporterPanel entity={active} key={active} />
        </div>
    );
}


function ImporterPanel({ entity }) {
    const inputRef = useRef(null);
    const meta = TABS.find((t) => t.key === entity);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [filename, setFilename] = useState("");
    const [dryRun, setDryRun] = useState(false);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get(`/admin/imports/${entity}/template`);
            const blob = new Blob([data.content], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = data.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to download template"); }
    };

    const upload = async (file) => {
        if (!file) return;
        setFilename(file.name); setBusy(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/admin/imports/${entity}${dryRun ? "?dry_run=true" : ""}`,
                fd, { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(data);
            toast.success(
                `${data.dry_run ? "DRY RUN · " : ""}` +
                `${data.created} would-be-created · ${data.updated} would-be-updated · ${data.errors} errors`
                    .replace(/would-be-/g, data.dry_run ? "would-be-" : "")
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-4 bg-card border border-border rounded-2xl p-5">
            <p className="text-xs text-muted-foreground">{meta?.help}</p>
            <div className="mt-4 flex gap-2 flex-wrap items-center">
                <button
                    data-testid={`download-template-${entity}`}
                    onClick={downloadTemplate}
                    className="bg-muted text-foreground border border-border rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                ><Download size={14} /> Download template</button>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden"
                    data-testid={`file-input-${entity}`}
                />
                <button
                    data-testid={`pick-file-${entity}`}
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    className={`rounded-xl px-4 py-2 text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2 ${dryRun ? "bg-amber-500 text-amber-950" : "bg-primary text-primary-foreground"}`}
                >{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {dryRun ? "Dry-run upload" : "Upload CSV"}</button>
                <label className="text-xs font-bold inline-flex items-center gap-2 ml-auto cursor-pointer">
                    <input
                        data-testid={`dry-run-toggle-${entity}`}
                        type="checkbox"
                        checked={dryRun}
                        onChange={(e) => setDryRun(e.target.checked)}
                        className="accent-amber-500 w-4 h-4"
                    />
                    Dry run (no database writes)
                </label>
                {filename && <span className="text-xs text-muted-foreground inline-flex items-center gap-1 basis-full sm:basis-auto"><FileSpreadsheet size={12} /> {filename}</span>}
            </div>

            {result?.dry_run && (
                <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2 text-xs font-bold inline-flex items-center gap-2">
                    <AlertCircle size={14} /> Dry run — nothing was written. Uncheck "Dry run" and re-upload to commit.
                </div>
            )}

            {result && (
                <div className="mt-5" data-testid={`result-${entity}`}>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3"><p className="text-emerald-700 font-black text-base" data-testid="result-created">{result.created}</p><p className="text-emerald-700/70 uppercase tracking-wider">Created</p></div>
                        <div className="bg-sky-50 border border-sky-100 rounded-xl p-3"><p className="text-sky-700 font-black text-base" data-testid="result-updated">{result.updated}</p><p className="text-sky-700/70 uppercase tracking-wider">Updated</p></div>
                        <div className="bg-rose-50 border border-rose-100 rounded-xl p-3"><p className="text-rose-700 font-black text-base" data-testid="result-errors">{result.errors}</p><p className="text-rose-700/70 uppercase tracking-wider">Errors</p></div>
                    </div>

                    <div className="mt-3 max-h-72 overflow-y-auto bg-muted/30 border border-border rounded-xl divide-y divide-border">
                        {(result.rows || []).map((r, idx) => (
                            <div key={idx} className="px-3 py-2 text-xs flex items-center gap-2">
                                {r.ok
                                    ? <CheckCircle2 size={14} className="text-emerald-600" />
                                    : <AlertCircle size={14} className="text-rose-600" />}
                                <span className="font-bold w-14 shrink-0">Row {r.row}</span>
                                <span className="flex-1 truncate">{r.ok ? r.action : r.error}</span>
                                {r.ok && r.id && <code className="text-[10px] opacity-60">{r.id.slice(0, 8)}…</code>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
